import type { PermissionInput, AppSubject, UserCapabilities } from '../types/auth'
import type { AppAction } from '@/types/shared-enums.generated'
import type { FeatureKey } from '../types/feature-keys.generated'
import type { CapKey, GlobalTipoCode } from '../lib/authz'

/** Exigência de uma rota. Igual para qualquer usuário — sem persona. */
export interface RouteGate {
  /**
   * Rank mínimo (hierárquico) — espelha o gate equivalente do backend quando
   * ele checa hierarquia (ex.: `isAtLeastSupervisor`). `number` ou o nome do
   * tier (`authz.ts`).
   */
  minRank?: number | GlobalTipoCode
  /**
   * Capability do modelo unificado (`authz.ts`/`caps`). Não confundir com
   * `requiredCapabilities` (sistema CASL antigo, `UserCapabilities`).
   */
  requiredCaps?: CapKey | CapKey[]
  /** Permissões CASL necessárias (uma ou várias). */
  requiredPermissions?: PermissionInput | PermissionInput[]
  /** 'all' (padrão): todas; 'any': ao menos uma. */
  permissionMode?: 'all' | 'any'
  /** Capacidades derivadas do CASL (`UserCapabilities`). */
  requiredCapabilities?: keyof UserCapabilities | Array<keyof UserCapabilities>
  /** Feature flags (enabled_features da empresa/CC). */
  requiredFeatures?: FeatureKey | FeatureKey[]
  /** Rota de leitura: acessível sem `can_edit_own_records` (Visualizador, clientes). */
  allowReadOnly?: boolean
}

export interface AccessCtx {
  hasRank(min: number | GlobalTipoCode): boolean
  hasCap(keys: CapKey | CapKey[]): boolean
  can(action: AppAction, subject: AppSubject): boolean
  capabilities: Partial<UserCapabilities> | null | undefined
  isFeatureEnabled(k: FeatureKey): boolean
}

/** `read-only` = negada só pelo confinamento de leitura (redireciona p/ painel/perfil). */
export type Access = 'ok' | 'read-only' | 'denied'

const asArray = <T,>(v: T | T[] | undefined): T[] =>
  v === undefined ? [] : Array.isArray(v) ? v : [v]

export function routeAccess(gate: RouteGate, ctx: AccessCtx): Access {
  if (!gate.allowReadOnly && !ctx.hasCap('can_edit_own_records')) return 'read-only'
  if (gate.minRank !== undefined && !ctx.hasRank(gate.minRank)) return 'denied'
  const caps = asArray(gate.requiredCaps)
  if (caps.length > 0 && !ctx.hasCap(caps)) return 'denied'
  const perms = asArray(gate.requiredPermissions)
  if (perms.length > 0) {
    const check = (p: PermissionInput) => {
      if (typeof p === 'string') {
        const [action, subject] = p.split(':') as [AppAction, AppSubject]
        return ctx.can(action, subject)
      }
      return ctx.can(p.action, p.subject)
    }
    const ok = gate.permissionMode === 'any' ? perms.some(check) : perms.every(check)
    if (!ok) return 'denied'
  }
  if (asArray(gate.requiredCapabilities).some((c) => ctx.capabilities?.[c] !== true))
    return 'denied'
  if (asArray(gate.requiredFeatures).some((k) => !ctx.isFeatureEnabled(k))) return 'denied'
  return 'ok'
}
